import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Select, Table, Pagination as Paging, Tooltip } from 'antd';
import { ModalSubTitle, ModalTitle } from '../../../components/text/Titles';
import { InboxFileRow, Client, Project, Inbox, Pagination } from '../../../types';
import { Dataset } from '../../../types/dataset';
import { getProjects } from '../../../lib/api/aws-api';
import { getClientInboxFileList, exportStagedInboxToDataset } from '../../../lib/api/inbox-api';
import { getDatasetsByProject } from '../../../lib/api/dataset-api';
import moment from 'moment';

const { Option } = Select;

const defaultPaging = {
  pageSize: 10,
  page: 0,
};

type Props = {
  visible: boolean;
  onClose: () => any;
  onSave: () => any;
  title: string;
  client: Client;
  inbox: Inbox;
  doctype: string;
  setExportingStaged: any;
  exportingStaged: boolean;
};

export const ExportStagedDocuments = ({
  visible,
  title,
  onClose,
  onSave,
  client,
  inbox,
  doctype,
  setExportingStaged,
  exportingStaged,
}: Props) => {
  const [paginationObject, setPaginationObject] = useState<Pagination>(defaultPaging);
  const [stagedFiles, setStagedFiles] = useState<InboxFileRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<number>();

  const queryString = '&status=STAGED&sort=time_created';

  const fetchFiles = useCallback(
    async (client, queryString, filter = null) => {
      const response = await getClientInboxFileList(client.io_customer_name, paginationObject, doctype, queryString);
      setStagedFiles(response.datasetInboxes);
      setLoading(false);
    },
    [setStagedFiles, doctype, paginationObject]
  );

  const fetchDatasets = async () => {
    const projects = await getProjects();
    setProjects(projects);
    Promise.all(
      projects.map(async (project: Project) => {
        const datasets = await getDatasetsByProject(project.id);
        setDatasets(prev => [...prev, ...datasets]);
      })
    );
  };

  useEffect(() => {
    fetchDatasets();
  }, []);

  useEffect(() => {
    if (client) fetchFiles(client, queryString);
  }, [client, fetchFiles, visible]);

  const exportStaged = async () => {
    if (selectedDataset) {
      setExportingStaged(true);
      await exportStagedInboxToDataset(selectedDataset, client.io_customer_name, doctype);
      setExportingStaged(false);
      setSelectedDataset(undefined);
      onSave();
    }
  };

  const closeModal = () => {
    setSelectedDataset(undefined);
    onClose();
  };

  return (
    <Modal
      style={{ minWidth: '580px' }}
      visible={visible}
      title={<ModalTitle>{title}</ModalTitle>}
      onCancel={closeModal}
      footer={
        <div>
          <Tooltip
            overlay={
              !selectedDataset ? 'Please select a dataset' : stagedFiles.length === 0 ? 'No files to export' : ''
            }>
            <Button
              disabled={!selectedDataset || stagedFiles.length === 0}
              loading={exportingStaged}
              style={{ marginRight: '8px' }}
              type="default"
              onClick={e => {
                exportStaged();
              }}>
              Export
            </Button>
          </Tooltip>
          <Button type="default" onClick={closeModal}>
            Cancel
          </Button>
        </div>
      }>
      <ModalSubTitle>Overview of all staged files ({parseInt(inbox?.nrofstageddocumentsforclass) || 0})</ModalSubTitle>
      <Table
        className="ant-table-toolbox"
        style={{ margin: '12px 0' }}
        dataSource={stagedFiles}
        rowKey={record => record.inbox_id}
        columns={[
          {
            title: 'Id',
            dataIndex: 'inbox_reference_run_id',
            key: 'inbox_reference_run_id',
            render: (text: number, item: InboxFileRow) => (
              <span
                style={{
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  width: '140px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {item.inbox_reference_run_id}
              </span>
            ),
          },
          {
            title: 'Date',
            dataIndex: 'time_created',
            key: 'time_created',
            render: (text: number, item: InboxFileRow) => (
              <span>{moment(item.inbox_time_created).format('ll HH:mm:ss')}</span>
            ),
          },
          {
            title: 'Score',
            dataIndex: 'score',
            key: 'score',
            render: (text: number, item: InboxFileRow) => <span>{(item.inbox_score * 100).toFixed()}%</span>,
          },
        ]}
        loading={loading}
        pagination={false}
      />
      <div style={{ margin: '16px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <Paging
          showSizeChanger={false}
          defaultCurrent={1}
          pageSize={defaultPaging.pageSize}
          total={parseInt(inbox?.nrofstageddocumentsforclass) || 0}
          onChange={page => {
            setPaginationObject({ ...defaultPaging, page: page - 1 });
          }}
        />
      </div>
      <ModalSubTitle>Destination Dataset</ModalSubTitle>
      <div>
        <Select
          style={{ width: 250, marginTop: '16px' }}
          placeholder="select a dataset"
          value={selectedDataset}
          onChange={setSelectedDataset}>
          {projects &&
            datasets &&
            datasets.map((dataset: Dataset) => (
              <Option key={dataset.id} value={dataset.id}>
                {' '}
                {projects.find(p => p.id === dataset.project_id)!.name} - {dataset.name}
              </Option>
            ))}
        </Select>
      </div>
    </Modal>
  );
};
